import type { FC } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'
import AddOutlined from '@mui/icons-material/AddOutlined'
import FolderOutlined from '@mui/icons-material/FolderOutlined'
import SearchOutlined from '@mui/icons-material/SearchOutlined'
import { WizardHeader } from '@/common/components'
import { toTitleCase } from '@/common/utils'
import type { DecryptedProject } from '../../api'

interface ProjectSelectStepProps {
  readonly hostname: string
  readonly projects: readonly DecryptedProject[]
  readonly isLoading: boolean
  readonly error: string | null
  readonly onSelect: (workDirectory: string) => void
  readonly onCancel: () => void
  readonly onRetry: () => void
}

export const ProjectSelectStep: FC<ProjectSelectStepProps> = ({
  hostname,
  projects,
  isLoading,
  error,
  onSelect,
  onCancel,
  onRetry,
}) => {
  const [searchQuery, setSearchQuery] = useState('')

  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isLoading && error === null) {
      searchRef.current?.focus()
    }
  }, [isLoading, error])

  const filteredProjects = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (query.length === 0) return projects

    return projects.filter((project) => {
      const displayName = toTitleCase(project.name).toLowerCase()
      const rawPath = project.path.toLowerCase()
      return displayName.includes(query) || rawPath.includes(query)
    })
  }, [projects, searchQuery])

  return (
    <Box sx={containerSx}>
      <WizardHeader
        icon={<AddOutlined sx={iconSx} />}
        title="Start New Session"
        description={
          <>
            Select a project on{' '}
            <Box component="span" sx={hostnameSx}>
              {hostname}
            </Box>
          </>
        }
      />

      {isLoading && (
        <Box sx={centeredSx}>
          <CircularProgress size={32} />
        </Box>
      )}

      {!isLoading && error !== null && (
        <Box sx={errorContainerSx}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}

      {!isLoading && error === null && projects.length === 0 && (
        <Box sx={centeredSx}>
          <Typography variant="body2" color="text.secondary">
            No project data exists for{' '}
            <Box component="span" sx={hostnameSx}>
              {hostname}
            </Box>
          </Typography>
        </Box>
      )}

      {!isLoading && error === null && projects.length > 0 && (
        <>
          <TextField
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            inputRef={searchRef}
            size="small"
            aria-label="Search projects"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={searchFieldSx}
          />

          {filteredProjects.length === 0 ? (
            <Box sx={centeredSx}>
              <Typography variant="body2" color="text.secondary">
                No matching projects
              </Typography>
            </Box>
          ) : (
            <List sx={listSx}>
              {filteredProjects.map((project) => {
                const displayName = toTitleCase(project.name)
                return (
                  <ListItemButton
                    key={project.path}
                    onClick={() => onSelect(project.path)}
                    aria-label={`Select project ${displayName}`}
                    sx={listItemSx}
                  >
                    <ListItemIcon sx={listItemIconSx}>
                      <FolderOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={displayName}
                      secondary={project.path}
                      secondaryTypographyProps={{ sx: secondaryTextSx }}
                    />
                  </ListItemButton>
                )
              })}
            </List>
          )}
        </>
      )}

      <Box sx={actionsSx}>
        <Button onClick={onCancel}>Cancel</Button>
      </Box>
    </Box>
  )
}

const iconSx: SxProps<Theme> = {
  color: 'info.main',
}

const hostnameSx: SxProps<Theme> = {
  color: 'info.main',
  fontWeight: 600,
}

const containerSx: SxProps<Theme> = {
  py: 2,
}

const centeredSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  py: 4,
}

const errorContainerSx: SxProps<Theme> = {
  mb: 2,
}

const searchFieldSx: SxProps<Theme> = {
  mb: 2,
}

const listSx: SxProps<Theme> = {
  maxHeight: 320,
  overflow: 'auto',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  p: 0,
}

const listItemSx: SxProps<Theme> = {
  py: 1.5,
}

const listItemIconSx: SxProps<Theme> = {
  minWidth: 36,
}

const secondaryTextSx: SxProps<Theme> = {
  color: 'text.secondary',
  wordBreak: 'break-all',
  fontSize: '0.75rem',
}

const actionsSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  mt: 3,
}
